import React from 'react';
import PropTypes from 'prop-types';
import renderIf from '@signal/utils/renderIf';
import componentPropType from '@signal/prop-types/component';
import { Table, createTableStyles, TableHead, TableBody, TableRow, TableCell, TableCellSticky, createTableCellStyles } from './styles';
export function TableListView(_ref) {
  var columnInfo = _ref.columnInfo,
      rows = _ref.rows,
      rowSpacing = _ref.rowSpacing,
      component = _ref.component,
      headerComponent = _ref.headerComponent,
      headerRowComponent = _ref.headerRowComponent,
      headerCellComponent = _ref.headerCellComponent,
      bodyComponent = _ref.bodyComponent,
      rowComponent = _ref.rowComponent,
      mapRowToProps = _ref.mapRowToProps,
      cellComponent = _ref.cellComponent,
      renderHeaderCell = _ref.renderHeaderCell,
      renderRowCell = _ref.renderRowCell,
      stickyHeaderCells = _ref.stickyHeaderCells;
  return /*#__PURE__*/React.createElement(Table, {
    component: component,
    style: createTableStyles(rowSpacing)
  }, renderIf(renderHeaderCell, function () {
    return /*#__PURE__*/React.createElement(TableHead, {
      component: headerComponent
    }, /*#__PURE__*/React.createElement(TableRow, {
      component: headerRowComponent
    }, columnInfo.map(function (column) {
      var key = column.key,
          width = column.width;
      var CellComponent = stickyHeaderCells ? TableCellSticky : TableCell;
      return /*#__PURE__*/React.createElement(CellComponent, {
        key: key,
        component: headerCellComponent,
        style: createTableCellStyles(width)
      }, renderHeaderCell(column));
    })));
  }), /*#__PURE__*/React.createElement(TableBody, {
    component: bodyComponent
  }, rows.map(function (row, rowIndex) {
    return /*#__PURE__*/React.createElement(TableRow, Object.assign({
      component: rowComponent
    }, mapRowToProps(row)), columnInfo.map(function (column) {
      var key = column.key,
          width = column.width;
      return /*#__PURE__*/React.createElement(TableCell, {
        key: key,
        component: cellComponent,
        style: createTableCellStyles(width)
      }, renderRowCell(row, column));
    }));
  })));
}
TableListView.propTypes = {
  /**
   * Description of columns. Must contain a key property and, optionally, a fixed
   * `width` for the column.
   */
  columnInfo: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })).isRequired,

  /**
   * An array of row data that will be used to render the rows. The `key` prop
   * is required, as it is a unique identifier to use as the render `key` for
   * each row. All other properties will be passed to the `renderRowCell`
   * function.
   */
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  })).isRequired,

  /**
   * Function used to render each header cell. It is optional and if it's not
   * provided, the header row will be hidden.
   * @type {Function}
   * @param {string} columnKey - the key for the column being rendered
   */
  renderHeaderCell: PropTypes.func,

  /**
   * Function used to render each row cell.
   * @type {Function}
   * @param {string} row - the current row object (from the `rows` array)
   * @param {string} columnKey - the key for the column being rendered
   */
  renderRowCell: PropTypes.func.isRequired,

  /**
   * An optional value that controls the spacing between the rows (defaults to 0).
   */
  rowSpacing: PropTypes.number,

  /**
   * An optional component to use for the root table element (defaults to "section").
   */
  component: componentPropType,

  /**
   * An optional component to use for the table header element (defaults to "div").
   */
  headerComponent: componentPropType,

  /**
   * An optional component to use for the header row element (defaults to "div").
   */
  headerRowComponent: componentPropType,

  /**
   * An optional component to use for the header cell elements (defaults to "div").
   */
  headerCellComponent: componentPropType,

  /**
   * An optional component to use for the table body element (defaults to "div").
   */
  bodyComponent: componentPropType,

  /**
   * An optional component to use for the body row element (defaults to "div").
   */
  rowComponent: componentPropType,

  /**
   * A function that transforms the row object into props that will be spread onto the row component.
   * defaults to ({ key }) => ({ key })
   */
  mapRowToProps: PropTypes.func,

  /**
   * An optional component to use for the row cell elements (defaults to "div").
   */
  cellComponent: componentPropType,

  /**
   * A boolean that determines whether the header cells should be position sticky or not. (Defaults to false)
   */
  stickyHeaderCells: PropTypes.bool
};
TableListView.defaultProps = {
  rowSpacing: 0,
  component: 'section',
  headerComponent: 'div',
  headerRowComponent: 'div',
  headerCellComponent: 'div',
  bodyComponent: 'div',
  rowComponent: 'div',
  mapRowToProps: function mapRowToProps(_ref2) {
    var key = _ref2.key;
    return {
      key: key
    };
  },
  cellComponent: 'div',
  stickyHeaderCells: false
};
export default TableListView;